<template>
  <div>
    <div class="formSection__Row--Select">
      <search-tag placeholder="Filter resources by tag" :lang="lang" :tags="tagOptions" v-model="tags" />
    </div>

    <div class="formSection__Row--Select">
      <label class="formLabel" :for="`resource-items-${_uid}`">{{ label }}</label>
      <div class="formSearch" v-if="multiple || (!multiple && resourceSelection.length === 0)">
        <div class="formSearch__InputWrapper">
          <icon class="formSearch__Icon" name="search"></icon>
          <p class="formSearch__Loader" v-if="loading">{{ $store.getters.getStringTranslation('loading') }}...</p>
          <input
            :id="`resource-items-${_uid}`"
            class="formInput--Search"
            type="text"
            @keyup="search.length > 2 || search.length === 0 ? getResourceOptions() : null"
            @click="getResourceOptions()"
            @focus="focused = true"
            @blur="focused = false"
            v-model="search"
            :placeholder="$store.getters.getStringTranslation('searchByName')"
          />
        </div>
        <ul class="formSearch__List" :class="{ 'is-Active': focused === true }" v-if="resourceOptions">
          <li class="formSearch__ListItem" v-for="resource in resourceOptions" :key="resource.id">
            <span @click="pushResource(resource)">
              <small v-if="types.length !== 1">{{ resource.type }}</small> {{ getTitle(resource) }}
              <template v-if="resource.status === 'draft'"> ({{ resource.status }}) </template>
            </span>
          </li>
          <li class="formSearch__ListItem" v-if="noResults === true">
            <span>No resources found</span>
          </li>
          <li class="formSearch__ListItem" v-else-if="resourceOptions.length === 0">
            <span>{{ $store.getters.getStringTranslation('loading') }}...</span>
          </li>
        </ul>
      </div>
      <ul
        class="modal__BuilderResources"
        :class="{ 'modal__BuilderResources--Slim': multiple === false }"
        v-if="resourceSelection.length > 0 && loadingSelection === false"
      >
        <li class="modal__BuilderResourcesItem" v-for="(resource, index) in resourceSelection" :key="resource.id">
          <template v-if="multiple">
            <button
              class="modal__BuilderResourcesIcon"
              @click="
                moveResource(index, index - 1, resourceSelection)
                moveResource(index, index - 1, ids)
              "
            >
              <icon name="arrow-up"></icon>
            </button>
            <button
              class="modal__BuilderResourcesIcon"
              @click="
                moveResource(index, index + 1, resourceSelection)
                moveResource(index, index + 1, ids)
              "
            >
              <icon name="arrow-down"></icon>
            </button>
          </template>
          <span
            ><small v-if="types.length !== 1">{{ resource.type }}</small> {{ getTitle(resource) }}</span
          >
          <button class="modal__BuilderResourcesIcon--Delete" @click="removeResource(resource)">
            <icon weight="regular" name="times"></icon>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import debounce from 'tiny-debounce'
import HTTP from '@/http-common.js'
import SearchTag from '@/components/editor/blocks/SearchTag.vue'
import MoveResource from '@/components/mixins/MoveResource.js'

export default {
  mixins: [MoveResource],
  components: {
    SearchTag,
  },
  props: {
    status: {
      type: String,
      default: 'published',
    },
    label: {
      type: String,
      default: 'Resources',
    },
    types: {
      type: Array,
      default: function () {
        return []
      },
    },
    lang: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    fetchResources: {
      type: Boolean,
      default: false,
    },
    quizId: {
      type: Array,
      default: function () {
        return []
      },
    },
    resources: {},
    apiKeys: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  name: 'SearchResource',
  data() {
    return {
      search: '',
      ids: [],
      tags: null,
      tagOptions: [],
      resourceSelection: [],
      resourceOptions: [],
      selectedResources: [],
      loading: false,
      loadingSelection: false,
      noResults: false,
      focused: false,
    }
  },
  watch: {
    ids() {
      if (this.multiple) {
        this.$emit('input', this.ids)
        this.$emit('change', this.resourceSelection)
      } else {
        this.$emit('input', this.ids[0] || null)
        this.$emit('change', this.resourceSelection[0] || null)
      }
    },
  },
  methods: {
    getTitle(resource) {
      if (resource) return resource.l10n.find((obj) => obj.lang === this.lang).title
      return null
    },

    // Resource actions
    getResourceOptions: debounce(function () {
      this.loading = true
      let limit = null
      if (this.search.length === 0) limit = 10

      let status = 'published'

      if (this.status === 'draft') {
        status = ['published', 'draft']
      }

      this.loading = true
      HTTP.post('search/resource', {
        api_keys: this.apiKeys,
        search_query: this.search,
        tags: this.tags,
        order_by: 'updated',
        order_dir: 'desc',
        types: this.types,
        is_backend: true,
        limit,
        status,
        lang: this.lang,
      }).then((response) => {
        this.loading = false
        this.resourceOptions = response.data.resources.filter(
          (a) => this.ids.indexOf(a.id) === -1 && a.l10n.find((b) => b.lang === this.lang)
        ) // Don't show options that are already selected)

        if (this.fetchResources && !this.resources) {
          this.resourceSelection = response.data.resources.filter((item) => item.id === this.quizId[0])
        }

        if (this.resourceOptions.length === 0) {
          this.noResults = true
        } else {
          this.noResults = false
        }
      })
    }, 500),

    pushResource(resource) {
      this.ids.push(resource.id)
      this.resourceSelection.push(resource)

      const index = this.resourceOptions.findIndex((x) => x.id === resource.id)
      this.resourceOptions.splice(index, 1)
      this.search = ''
    },

    removeResource(resource) {
      this.resourceOptions.push(resource)

      const index = this.resourceSelection.findIndex((x) => x.id === resource.id)
      this.ids.splice(index, 1)
      this.resourceSelection.splice(index, 1)
    },
  },
  mounted() {
    if (this.fetchResources) {
      this.getResourceOptions()
    }
    if (this.resources === undefined || this.resources === null) {
      this.resourceOptions = []
    } else if (typeof this.resources === 'object' && !Array.isArray(this.resources)) {
      this.resourceSelection = [this.resources]
      if (this.resources) this.ids = [this.resources.id]
    } else if (this.resources && this.resources.length > 0 && typeof this.resources[0] === 'object') {
      this.resourceSelection = this.resources
      this.ids = this.resources.map((resource) => resource.id)
    } else if (this.resources && this.resources.length > 0 && typeof this.resources[0] === 'number') {
      this.ids = this.resources
      this.resources.forEach((id) => {
        this.loadingSelection = true
        HTTP.post(`resource/${id}`, {
          api_keys: this.apiKeys,
        }).then((response) => {
          this.resourceSelection.push(response.data)
          this.loadingSelection = false
        })
      })
    }
  },
}
</script>
